/**
 * Chrome — the PERSISTENT app frame. The header (home · breadcrumb · conversation
 * · tenant · theme · profile) is ALWAYS rendered, on every route, so you can always
 * get home / to profile. It owns the channel (so the conversation section + stream
 * scope work everywhere) and renders the routed body via <Outlet>.
 *
 * Routes render INSIDE this layout (see ChannelApp). The body reads the channel +
 * scope from the outlet context (useChromeContext).
 */
import React, { useEffect, useState, useSyncExternalStore } from "react";
import { Outlet, useOutletContext } from "react-router-dom";
import { User as UserIcon, Sun, Moon, Star, Menu, X } from "lucide-react";

import type { ActiveOrg, User } from "@/schema";
import { authenticateService } from "../services/AuthenticateService";
import { AdminUserSwitcher } from "../components/AdminUserSwitcher";
import { TenantSwitcher } from "../components/TenantSwitcher";
import { IntegrationManager } from "../components/IntegrationManager";
import UserProfile from "../pages/UserProfile";
import { useTheme } from "../hooks/useTheme";

import { useChannel, type ChannelApi } from "./useChannel";
import { ConversationSection } from "./ConversationSection";
import { FeedToasts } from "./FeedToasts";
import { InboxBell } from "./InboxBell";
import { InboxPanel } from "./InboxPanel";
import { feedStore, useFeedStoreState } from "./feedStore";
import { feedRailAnnounce, lucideIconOfWord, modeCurrentAnnounce } from "./feedWave";
import { OPEN_AUTH_EVENT } from "./AnnouncementStream";
import { CommandBar } from "./CommandBar";
import type { Ref } from "./contract";
import type { Announcement, BoxAction } from "./contract";
import { artifactLifecycleCancelAnnounce, type ArtifactInteractionKind } from "./artifactInteraction";
import { routeAgentIntent } from "./agentSurface";
import { routeTaskAskInteraction } from "./taskAsk";
import type { ConversationFeedItem } from "./client";
import { Box } from "./Box";
import {
  artifactMachineRefOf,
  announcementArtifactMachineIdOf,
  hasArtifactMachine,
  payloadArtifactOf,
  renderArtifactOf,
} from "./artifactMachine";
import { ConversationWorkspaceProvider, prioritizedAnnouncementsOf, useConversationWorkspace } from "./ConversationWorkspace";
import { queryKindForFeed, queryRefsForFeed } from "./feedRefs";
import { toneByIndex } from "./tones";

// THE RAIL IS MORE IMPORTANT (Ori 2026-07-24): an open rail takes a bigger
// share of the width; open/close is decoupled from the conversation axis.
const SECTION_WEIGHTS = {
  left: 30,
  middle: 40,
  right: 30,
} as const;

// THE command-bar clearance — the ONE place that defines it. The command bar
// overlays the bottom of the 3-section grid; the grid publishes this as
// --command-clearance and every section's scroll (.workspace-section-scroll)
// pads its bottom by it BLINDLY, so long content scrolls past the bar instead
// of hiding behind it. Never hardcode a bottom padding in a section.
const COMMAND_CLEARANCE = "89px";

export interface ChromeContext {
  ch: ChannelApi;
  scope: Ref[];
  setScope: (s: Ref[]) => void;
  scopePath: string;
  conversationId: string;
  setConversationId: (id: string, label?: string) => void;
}

export const useChromeContext = () => useOutletContext<ChromeContext>();

export const Chrome: React.FC<{
  user: User;
  activeUser: User;
  activeOrg: ActiveOrg | null;
}> = ({ user, activeUser, activeOrg }) => {
  const { theme, setTheme } = useTheme();
  // the bearer read FROM the one owner (AuthenticateService over the session
  // snapshot) — no local token copy; refresh/clear notify through subscribe.
  const token = useSyncExternalStore(
    (onChange) => authenticateService.subscribe(onChange),
    () => { try { return authenticateService.getToken(); } catch { return ""; } },
  );

  const [channelConversationId, setChannelConversationId] = useState("");
  const ch = useChannel({ userId: activeUser?.id, token }, channelConversationId);
  const [connectorCatalogOpen, setConnectorCatalogOpen] = useState(false);
  const [connectorFocusServerId, setConnectorFocusServerId] = useState<string | undefined>(undefined);

  // P4-P5: an openAuth box (from an @auth announcement) → open IntegrationManager
  // pre-targeted to that server, SKIPPING the catalog. Chrome is persistent, so the
  // auth modal opens from any route; IntegrationManager auto-opens the server's connect.
  useEffect(() => {
    const onOpenAuth = (e: Event) => {
      const id = (e as CustomEvent).detail?.serverId;
      if (id) {
        setConnectorFocusServerId(String(id));
        setConnectorCatalogOpen(true);
      }
    };
    window.addEventListener(OPEN_AUTH_EVENT, onOpenAuth);
    return () => window.removeEventListener(OPEN_AUTH_EVENT, onOpenAuth);
  }, []);

  // Conversation-OPEN positioning (openConversation → onJump): the view must
  // OPEN at its position — instant snap, never an animated render-then-lurch.
  // Smooth scrolling belongs only to live arrivals / in-view toast jumps.
  const jumpTo = (id: string) =>
    document.getElementById(`box-${id}`)?.scrollIntoView({ behavior: "auto", block: "start" });
  return (
    <ConversationWorkspaceProvider
      ch={ch}
      // the rail open states are WORLD STATE (feed.left / feed.right —
      // sci//feed:left:@binary / sci//feed:right:@binary);
      // conversation activation never force-opens a panel client-side.
      onOpenMain={() => undefined}
      onJump={jumpTo}
      onActiveConversationId={setChannelConversationId}
    >
      <ChromeFrame
        user={user}
        activeUser={activeUser}
        activeOrg={activeOrg}
        ch={ch}
        theme={theme}
        setTheme={setTheme}
        connectorCatalogOpen={connectorCatalogOpen}
        setConnectorCatalogOpen={setConnectorCatalogOpen}
        connectorFocusServerId={connectorFocusServerId}
        setConnectorFocusServerId={setConnectorFocusServerId}
      />
    </ConversationWorkspaceProvider>
  );
};

const ChromeFrame: React.FC<{
  user: User;
  activeUser: User;
  activeOrg: ActiveOrg | null;
  ch: ChannelApi;
  theme: "dark" | "light";
  setTheme: (theme: "dark" | "light") => void;
  connectorCatalogOpen: boolean;
  setConnectorCatalogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  connectorFocusServerId?: string;
  setConnectorFocusServerId: React.Dispatch<React.SetStateAction<string | undefined>>;
}> = ({
  user,
  activeUser,
  activeOrg,
  ch,
  theme,
  setTheme,
  connectorCatalogOpen,
  setConnectorCatalogOpen,
  connectorFocusServerId,
  setConnectorFocusServerId,
}) => {
  const workspace = useConversationWorkspace();
  const [profileOpen, setProfileOpen] = useState(false);
  // THE FEED ROW drives the chrome: the two rail binaries, tab tokens, mode,
  // the prioritized list, and the served button icon words all come from the
  // ONE channel store.
  const feed = useFeedStoreState();
  const channelReady = ch.ready;
  const channelQuery = ch.query;
  useEffect(() => {
    if (!channelReady) return;
    void feedStore.ensureArrangement(channelQuery);
  }, [channelQuery, channelReady]);

  // ── THEME FROM THE WORLD: the served feed.current drives dark/light; the
  // toggle walks the SERVED mode options, applies optimistically, and writes
  // the mode-current lane (sci//feed:@(ui:kind:mode):current) — the next
  // reconcile trues it up.
  const servedCurrent = feed.feed?.current;
  useEffect(() => {
    if (servedCurrent === "dark" || servedCurrent === "light") setTheme(servedCurrent);
  }, [servedCurrent, setTheme]);
  const onModeToggle = () => {
    const options = feed.feed?.mode.length ? feed.feed.mode : ["dark", "light"];
    const next = options[(options.indexOf(theme) + 1) % options.length] || (theme === "dark" ? "light" : "dark");
    if (next !== "dark" && next !== "light") {
      console.error(`[feed] served mode option '${next}' is not a renderable theme — refusing the switch`);
      return;
    }
    setTheme(next);
    feedStore.applyModeCurrent(next);
    if (ch.userId) {
      void ch.announce(modeCurrentAnnounce(ch.userId, next))
        .catch((error) => console.error("[feed] the mode write failed (never reached the world):", error));
    }
  };

  // ── THE RAILS (the confirm-loop wave, Ori 2026-07-25) — the LEFT side is
  // ONE overlay: NOTHING, the INBOX, or the TAB strip — never both
  // (sci//feed:@binary:@inbox XOR sci//feed:@binary:@tab**; the shared
  // feed:left:@binary died). The RIGHT side keeps its own binary
  // (sci//feed:right:@binary — the prioritized list). Open/close is
  // decoupled from the conversation. Optimistic fold first, the write
  // reconciles; opening one overlay closes the other by construction.
  const leftOverlay = feed.feed?.left ?? "";
  const leftOpen = leftOverlay !== "";
  const tabOpen = leftOverlay === "tab";
  const rightOpen = feed.feed?.right === true;
  // THE CLICK DECLARES (Ori 2026-08-04) — the store resolves the absolute next
  // value from its LIVE projection (standing declaration over serve) and folds
  // it in one step, so the announce and the fold always carry the SAME
  // boolean. Deriving `next` here from the render snapshot inverted a click
  // that landed before its own write came back.
  const toggleRail = (lane: "inbox" | "tab" | "right") => {
    const next = feedStore.declareRail(lane);
    if (ch.userId) {
      void ch.announce(feedRailAnnounce(ch.userId, lane, next))
        .catch((error) => console.error("[feed] the rail write failed (never reached the world):", error));
    }
  };

  const bodyGridTemplate = leftOpen && rightOpen
    ? `${SECTION_WEIGHTS.left}fr ${SECTION_WEIGHTS.middle}fr ${SECTION_WEIGHTS.right}fr`
    : leftOpen
      ? `${SECTION_WEIGHTS.left}fr ${SECTION_WEIGHTS.middle + SECTION_WEIGHTS.right}fr`
      : rightOpen
        ? `${SECTION_WEIGHTS.left + SECTION_WEIGHTS.middle}fr ${SECTION_WEIGHTS.right}fr`
        : "1fr";
  const TabStripIcon = lucideIconOfWord(feed.feed?.icon.tab ?? "") ?? Menu;
  const ModeIcon = lucideIconOfWord(feed.feed?.icon.mode ?? "") ?? (theme === "dark" ? Sun : Moon);
  const PrioritizedIcon = lucideIconOfWord(feed.feed?.icon.prioritized ?? "") ?? Star;
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <header style={headerStyle}>
        {/* Left cluster — conversation section toggle + active conversation. */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
          <button
            onClick={() => toggleRail("tab")}
            title="Conversation section"
            aria-expanded={tabOpen}
            style={{ ...iconBtn, background: tabOpen ? "var(--color-surface)" : "transparent" }}
          >
            <TabStripIcon size={18} />
          </button>
          {/* THE INBOX — Ori's placement: beside the menu button, the LEFT
              cluster's right edge — never the right controls. */}
          <InboxBell ch={ch} />
          <div title="Conversation" style={conversationLabel}>
            {workspace.machine?.label || workspace.displayLabel || ""}
          </div>
        </div>
        {/* Right cluster — operators first, identity closest to the edge. */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={onModeToggle} style={iconBtn} title="Theme">
            <ModeIcon size={18} />
          </button>
          <button
            onClick={() => toggleRail("right")}
            style={{ ...iconBtn, background: rightOpen ? "var(--color-surface)" : "transparent" }}
            title="Prioritized"
            aria-expanded={rightOpen}
          >
            <PrioritizedIcon size={18} />
          </button>
          <div style={divider} />
          <AdminUserSwitcher
            user={user}
            activeUser={activeUser}
            compact
          />
          <TenantSwitcher
            user={activeUser}
            activeOrg={activeOrg}
            compact
          />
          <button
            onClick={() => setProfileOpen((v) => !v)}
            style={{ ...iconBtn, background: profileOpen ? "var(--color-surface)" : "transparent" }}
            title="Profile"
            aria-expanded={profileOpen}
          >
            <UserIcon size={18} />
          </button>
        </div>
      </header>

      {/* Routed body — a flex:1 column with a definite height so Shell can pin its
          command bar and the Profile page (h-full overflow-y-auto) can scroll. */}
      <main style={{ ...mainStyle, gridTemplateColumns: bodyGridTemplate, "--command-clearance": COMMAND_CLEARANCE } as React.CSSProperties}>
        {/* THE LEFT RAIL — ONE overlay (the confirm-loop wave): the INBOX or
            the TAB strip, never both at once (sci//feed:@binary:@inbox XOR
            sci//feed:@binary:@tab**). The bell opens the live inbox; the menu
            opens the conversations/tab strip. */}
        {leftOpen && (
          <div style={{ display: "grid", gridTemplateRows: "minmax(0, 1fr)", minHeight: 0, overflow: "hidden" }}>
            {leftOverlay === "inbox"
              ? <InboxPanel ch={ch} />
              : <ConversationSection ch={ch} isPlatformAdmin={user.type === "admin"} />}
          </div>
        )}
        <WorkspaceSection edge="middle">
          <Outlet context={{
            ch,
            scope: workspace.scope,
            setScope: workspace.setScope,
            scopePath: workspace.scopePath,
            conversationId: workspace.conversationId,
            setConversationId: workspace.setActiveConversationId,
          } as ChromeContext} />
        </WorkspaceSection>
        {rightOpen && (
          <PrioritizedRail
            ch={ch}
          />
        )}
        <div style={commandOverlay}>
          <CommandBar
            ch={ch}
            conversationId={workspace.conversationId}
            setConversationId={workspace.setActiveConversationId}
            onNewConnector={() => {
              setConnectorFocusServerId(undefined);
              setConnectorCatalogOpen(true);
            }}
          />
        </div>
      </main>

      {/* THE FEED WAVE toasts — the responsive layer (live ws frames for other
          conversations that are inbox-worthy); rendered in the persistent
          frame so they pop on every route. */}
      <FeedToasts ch={ch} />

      {/* Profile overlay — fixed right-side panel, does not shift the grid. */}
      {profileOpen && (
        <>
          <div onClick={() => setProfileOpen(false)} style={profileScrim} />
          <div className="profile-panel" style={profilePanel} role="dialog" aria-label="Profile" aria-modal="true">
            <div style={profilePanelHeader}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-secondary)", letterSpacing: "0.01em" }}>Profile</span>
              <button
                onClick={() => setProfileOpen(false)}
                style={profileCloseBtn}
                title="Close profile"
                aria-label="Close profile"
              >
                <X size={15} />
              </button>
            </div>
            <UserProfile user={activeUser} activeOrg={activeOrg!} channel={ch} />
          </div>
        </>
      )}

      {/* openAuth → the integration auth modal, PRE-TARGETED to the server (catalog skipped). */}
      {connectorCatalogOpen && (
        <IntegrationManager
          isOpen
          variant="addModal"
          focusServerId={connectorFocusServerId}
          conversationId={workspace.conversationId}
          onRegisteredSurfaces={async (result) => {
            const expectedSurfaceRefs = new Set(
              result.surfaces.flatMap((surface) => [
                surface.surfaceId,
                surface.providerSurfaceId,
                surface.providerSurfaceName,
                surface.providerSurfaceLabel,
              ].filter((value): value is string => typeof value === "string" && value.length > 0)),
            );
            const matchesRegisteredSurface = (item: ConversationFeedItem): boolean => {
              const artifact = item.artifact as {
                machine?: { id?: string; name?: string; label?: string };
                input?: {
                  surface?: {
                    surfaceId?: string;
                    providerSurfaceId?: string;
                    providerSurfaceName?: string;
                    providerSurfaceLabel?: string;
                  };
                };
              } | undefined;
              const machine = artifact?.machine;
              const machineId = String(machine?.id || machine?.name || "");
              if (!machineId.startsWith("connector_surface_")) return false;
              const surface = artifact?.input?.surface || {};
              const refs = [
                surface.surfaceId,
                surface.providerSurfaceId,
                surface.providerSurfaceName,
                surface.providerSurfaceLabel,
                machine?.label,
                machineId,
              ].filter((value): value is string => typeof value === "string" && value.length > 0);
              return refs.some((ref) => expectedSurfaceRefs.has(ref))
                || refs.some((ref) => [...expectedSurfaceRefs].some((expected) => ref.includes(expected)));
            };
            let item: ConversationFeedItem | undefined;
            let latestItems: ConversationFeedItem[] = [];
            for (let attempt = 0; attempt < 8 && !item; attempt += 1) {
              if (attempt > 0) await new Promise((resolve) => window.setTimeout(resolve, 500));
              const feed = await ch.query({
                tab: "integrations",
                refs: queryRefsForFeed("integrations"),
                kind: queryKindForFeed("integrations"),
                limit: 60,
              });
              if (Array.isArray(feed.conversationContext?.history)) {
                ch.hydrateAnnouncements(feed.conversationContext.history);
              }
              latestItems = feed.items || [];
              item = latestItems.find(matchesRegisteredSurface);
            }
            if (item) {
              workspace.openConversation(item);
            }
          }}
          onConnectorIntent={async (intent) => {
            const intentMachine = {
              name: "connector-intent",
              id: `connector-intent:${Date.now().toString(36)}`,
              label: intent.label || intent.query || intent.serverName || "Connector",
              tags: ["Connector", "Intent"],
              status: "start" as const,
            };
            await ch.announce({
              type: "connector.intent",
              conversationId: workspace.conversationId,
              machine: intentMachine,
              ref: [
                "sci*//integration:instance:@instance**",
                `sci//instance:@database:@${intent.kind}**`,
              ],
              artifact: [{
                machine: intentMachine,
                suffix: "md",
                position: "match",
                input: {
                  kind: "connector.intent",
                  data: intent,
                },
                params: [],
              }],
              evidence: [{
                kind: "connector.intent",
                confidence: 1,
                body: {
                  data: intent,
                },
              }],
            });
          }}
          onClose={() => {
            setConnectorCatalogOpen(false);
            setConnectorFocusServerId(undefined);
          }}
        />
      )}
    </div>
  );
};

const WorkspaceSection: React.FC<{
  edge: "middle" | "right";
  children: React.ReactNode;
}> = ({ edge, children }) => (
  <section className="workspace-section" data-edge={edge}>
    {children}
  </section>
);

const SectionState: React.FC<{
  kind: "empty" | "error" | "loading";
  children: React.ReactNode;
}> = ({ kind, children }) => (
  <div className="workspace-section-state" data-kind={kind}>
    {children}
  </div>
);

const PrioritizedRail: React.FC<{
  ch: ChannelApi;
}> = ({ ch }) => {
  const workspace = useConversationWorkspace();
  const feed = useFeedStoreState();
  const { selectedId } = workspace.prioritized;
  // THE PRIORITIZED LIST IS FEED-LEVEL (the right rail — feed:right:
  // @prioritized**): the locally-held announcements flagged on write
  // (body.prioritized) render as full boxes; world feed entries whose
  // announcement is not held locally render as conversation-open pointers
  // (the tab-red rides the conversation, so the entry keeps its place).
  const items = prioritizedAnnouncementsOf(ch.announcements);
  const heldArtifactIds = new Set(items.map((a) => announcementArtifactMachineIdOf(a)));
  const worldOnlyEntries = (feed.feed?.prioritized ?? [])
    .filter((entry) => !heldArtifactIds.has(entry.artifact));
  const selectedAnnouncement = items.find((a) => announcementArtifactMachineIdOf(a) === selectedId) || items[0];
  const selectedIndex = selectedAnnouncement
    ? Math.max(0, items.findIndex((a) => a.id === selectedAnnouncement.id))
    : 0;
  const selectedArtifactMachineId = selectedAnnouncement ? announcementArtifactMachineIdOf(selectedAnnouncement) : "";
  const selectedArtifactVisibleInStream = !!(
    selectedArtifactMachineId && workspace.streamArtifactVisible[selectedArtifactMachineId]
  );

  const onPrioritizedAction = async (action: BoxAction, a: Announcement) => {
    if (action === "opened" || action === "hovered" || action === "clicked" || action === "selected") {
      const artifact = payloadArtifactOf(a);
      if (hasArtifactMachine(artifact)) {
        void ch.trackArtifactInteraction({ artifact, announcement: a, interaction: action }).catch((error) => console.error("[artifact-interaction] claim failed (the interaction evidence never reached the world):", error));
      }
      return;
    }
    if (action === "prioritize" || action === "unprioritize") {
      const artifact = renderArtifactOf(a);
      await ch.prioritizeArtifact({
        conversationId: a.conversationId,
        artifact,
        artifactId: artifactMachineRefOf(artifact) || undefined,
        active: action === "prioritize",
        label: a.machine?.label || a.conversationId || "Conversation",
      });
      const trackableArtifact = payloadArtifactOf(a);
      if (hasArtifactMachine(trackableArtifact)) {
        void ch.trackArtifactInteraction({
          artifact: trackableArtifact,
          announcement: a,
          interaction: "@prioritized",
          active: action === "prioritize",
        }).catch((error) => console.error("[artifact-interaction] claim failed (the interaction evidence never reached the world):", error));
      }
    }
  };

  // LIFECYCLE CANCEL (control tab) — the same HUMAN-session announce the
  // stream wires: sci//lifecycle:cancel on the ref lane retires the stored
  // secret at the outlet, then crosses the lifecycle transition to world.
  const onPrioritizedCancelArtifact = async (a: Announcement) => {
    await ch.announce(artifactLifecycleCancelAnnounce(payloadArtifactOf(a) ?? renderArtifactOf(a)));
  };

  const onPrioritizedArtifactInteraction = (
    interaction: ArtifactInteractionKind,
    a: Announcement,
    detail?: Record<string, unknown>,
  ) => {
    const artifact = payloadArtifactOf(a);
    if (hasArtifactMachine(artifact)) {
      // the prioritized rail renders the SAME artifacts the stream does — a task
      // queue ADD submitted here rides the identical channel command path
      // (the ONE routing owner; the declared lane atom decides). The agent
      // artifacts' re-assign/create intents ride the sibling owner.
      if (!routeTaskAskInteraction(ch.announce, a, artifact, interaction, detail)) {
        routeAgentIntent(ch.announce, a, artifact, interaction, detail);
      }
      void ch.trackArtifactInteraction({ artifact, announcement: a, interaction, detail }).catch((error) => console.error("[artifact-interaction] claim failed (the interaction evidence never reached the world):", error));
    }
  };

  return (
    <WorkspaceSection edge="right">
      <aside style={prioritizedRailStyle} aria-label="Prioritized">
      <div className="workspace-section-scroll" style={prioritizedRailList}>
        {items.length === 0 && worldOnlyEntries.length === 0 && <SectionState kind="empty">Nothing prioritized yet.</SectionState>}
        {items.length > 0 && (
          <div style={prioritizedNav} aria-label="Prioritized navigation">
            {items.map((a, index) => {
              const active = a.id === selectedAnnouncement?.id;
              const tone = toneByIndex(index);
              const label = a.machine?.label || a.machine?.name || `prioritized ${index + 1}`;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => workspace.setSelectedPrioritizedId(announcementArtifactMachineIdOf(a))}
                  style={{
                    ...prioritizedNavButton,
                    background: active ? tone.accent : tone.tint,
                    borderColor: tone.accent,
                    color: active ? "#fff" : tone.accent,
                  }}
                  title={label}
                  aria-current={active ? "true" : undefined}
                >
                  <Star size={12} aria-hidden="true" />
                  {label}
                </button>
              );
            })}
          </div>
        )}
        {/* world feed entries whose announcement is not held locally —
            conversation-open pointers (the entry keeps its place). */}
        {worldOnlyEntries.length > 0 && (
          <div style={prioritizedNav} aria-label="Prioritized elsewhere">
            {worldOnlyEntries.map((entry, index) => {
              const tone = toneByIndex(items.length + index);
              const label = entry.label[0] || entry.artifact;
              return (
                <button
                  key={`world-${entry.artifact}`}
                  type="button"
                  onClick={() => {
                    if (entry.conversation) {
                      workspace.setActiveConversationId(entry.conversation, label);
                    }
                  }}
                  style={{
                    ...prioritizedNavButton,
                    background: tone.tint,
                    borderColor: tone.accent,
                    color: tone.accent,
                  }}
                  title={label}
                >
                  <Star size={12} aria-hidden="true" />
                  {label}
                </button>
              );
            })}
          </div>
        )}
        {selectedAnnouncement && (
          <div style={prioritizedBoxWrap}>
            <Box
              a={selectedAnnouncement}
              onAction={onPrioritizedAction}
              onArtifactInteraction={onPrioritizedArtifactInteraction}
              accent={toneByIndex(selectedIndex).accent}
              hydrateArtifact={ch.hydrateArtifact}
              query={ch.query}
              onCancelArtifact={onPrioritizedCancelArtifact}
              artifactSurfaceSuppressed={selectedArtifactVisibleInStream}
            />
          </div>
        )}
      </div>
      </aside>
    </WorkspaceSection>
  );
};

const headerStyle: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 12, padding: "10px 16px",
  borderBottom: "1px solid var(--color-border)",
  flexShrink: 0,
  background: "var(--color-bg)",
};
const iconBtn: React.CSSProperties = {
  position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: 34, height: 34, borderRadius: 10, border: "none", background: "transparent",
  cursor: "pointer", color: "var(--color-text-secondary)",
};
const divider: React.CSSProperties = {
  width: 1, height: 16, margin: "0 2px", background: "var(--color-border)",
};
const mainStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: "grid",
  minWidth: 0,
  position: "relative",
  overflow: "hidden",
};
const conversationLabel: React.CSSProperties = {
  minWidth: 0,
  maxWidth: 220,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: "var(--color-text-secondary)",
  fontSize: 13,
  fontWeight: 600,
  lineHeight: "18px",
};
const prioritizedRailStyle: React.CSSProperties = {
  width: "100%",
  flex: 1,
  height: "100%",
  background: "var(--color-bg)",
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
};
const commandOverlay: React.CSSProperties = {
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 40,
};
const prioritizedRailList: React.CSSProperties = {
  display: "grid",
  gridTemplateRows: "auto minmax(0, 1fr)",
  alignContent: "start",
  gap: 10,
  // bottom padding comes from .workspace-section-scroll (--command-clearance)
  paddingTop: 12,
  paddingInline: 12,
  overflowY: "auto",
  overflowX: "hidden",
  minHeight: 0,
};
const prioritizedNav: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(74px, 1fr))",
  gap: 6,
};
const prioritizedNavButton: React.CSSProperties = {
  minWidth: 0,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  border: "1px solid var(--color-border)",
  borderRadius: 7,
  background: "transparent",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontSize: 10,
  fontWeight: 650,
  padding: "0 8px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const prioritizedBoxWrap: React.CSSProperties = {
  minHeight: 0,
  width: "100%",
};
const profileScrim: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 50,
  background: "rgba(0,0,0,0.25)",
};
const profilePanel: React.CSSProperties = {
  position: "fixed",
  right: 0,
  top: 0,
  bottom: 0,
  zIndex: 51,
  background: "var(--color-bg)",
  borderLeft: "1px solid var(--color-border)",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
};
const profilePanelHeader: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 16px",
  height: 55,
  background: "var(--color-bg)",
  borderBottom: "1px solid var(--color-border)",
  flexShrink: 0,
};
const profileCloseBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 34,
  height: 34,
  borderRadius: 10,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "var(--color-text-secondary)",
};
